import { Box, Typography } from "@mui/material";
import { OpenInFull } from "@mui/icons-material";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { useState } from "react";
import ChartModal from "./ChartModal";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export function RankingsList({ title, data, onExpandClicked }) {
  // Sort ascending (rank 1 on top)
  const sorted = [...data].sort((a, b) => a.rank - b.rank);

  return (
    <MetricCard>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "1.1rem",
          mb: 1,
          color: "text.primary",
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "3px",
          },
        }}
      >
        {sorted.map((item, index) => (
          <Box
            key={item.name}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderRadius: 1,
              bgcolor: index === 0 ? "primary.light" : "background.default",
              color: index === 0 ? "white" : "text.secondary",
              fontWeight: index === 0 ? 700 : 500,
              transition: "0.2s ease",
              "&:hover": {
                bgcolor: index === 0 ? "primary.main" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Typography>{item.name}</Typography>
            <Typography>#{item.rank}</Typography>
          </Box>
        ))}
      </Box>
      {onExpandClicked && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Box
            sx={{
              display: "flex",
              borderRadius: 1,
              transition: "0.2s ease",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.05)",
              },
              py: 0.25,
              px: 0.5,
              cursor: "pointer",
              position: "absolute",
              bottom: 4,
              right: 4,
            }}
            onClick={onExpandClicked}
          >
            <OpenInFull sx={{ width: 16, height: 16 }}></OpenInFull>
            <Typography variant="h6" component="h6" sx={{ cursor: "pointer" }}>
              Expand
            </Typography>
          </Box>
        </Box>
      )}
    </MetricCard>
  );
}

export function MetricStat({ title, value }) {
  return (
    <MetricCard>
      <Typography
        sx={{
          color: "black",
          fontWeight: 600,
          fontSize: "1.1rem",
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          p: 2,
        }}
      >
        <Typography
          sx={{
            color: "primary.main",
            fontWeight: 600,
            fontSize: "2rem",
            textAlign: "right",
          }}
        >
          {value}
        </Typography>
      </Box>
    </MetricCard>
  );
}

export function MetricCard({ children }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: "background.paper",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        pb: 4,
      }}
    >
      {children}
    </Box>
  );
}

export function MetricChart({ type, data, options, onExpandClicked }) {
  return (
    <MetricCard>
      {type === "bar" && <Bar data={data} options={options} />}
      {type === "line" && <Line data={data} options={options} />}
      {type === "pie" && <Pie data={data} options={options} />}
      {onExpandClicked && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <Box
            sx={{
              display: "flex",
              borderRadius: 1,
              transition: "0.2s ease",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.05)",
              },
              py: 0.25,
              px: 0.5,
              cursor: "pointer",
              position: "absolute",
              bottom: 4,
              right: 4,
            }}
            onClick={onExpandClicked}
          >
            <OpenInFull sx={{ width: 16, height: 16 }}></OpenInFull>
            <Typography variant="h6" component="h6" sx={{ cursor: "pointer" }}>
              Expand
            </Typography>
          </Box>
        </Box>
      )}
    </MetricCard>
  );
}
